import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TENANT_STATUS_MAP } from '@goyone/shared';
import { api } from '../lib/api';
import { useAuth, useSession } from '../auth/AuthProvider';
import { Icon } from '../components/Icon';
import { NotificationBell, NotificationPopups } from '../components/Notifications';
import { WorkTimerBadge } from '../components/WorkTimer';
import { CheckClock } from '../components/CheckClock';
import { Logo } from '../components/Logo';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { SubscriptionBanner } from '../components/SubscriptionBanner';
import { Badge, Modal } from '../components/ui';
import { date, initials } from '../lib/format';
import { useActivityHeartbeat } from '../lib/heartbeat';
import { ScreenMonitorProvider } from '../components/ScreenMonitor';
import { ChatDock } from '../components/ChatDock';
import { JoyMark } from '../components/JoyPanel';
import { Menu } from '../components/Menu';
import { useI18n } from '../lib/i18n';
import { APPS, appForPath } from './nav';
import { useStickyTableFit } from '../lib/stickyTableFit';
import { desktop } from '../lib/desktop';
import { WorkModeBadge, WorkModeSwitch } from '../components/WorkProfiles';

/** ما طواه المستخدمُ من مجموعات القائمة — يبقى مطويًّا بين الجلسات. */
const CLOSED_GROUPS_KEY = 'goyone:nav-closed';

/* الحفظُ لا يمنع الطيّ: تخزينٌ محظورٌ يعني جلسةً واحدةً لا عطبًا. */
function writeClosed(next: Set<string>): void {
  try {
    localStorage.setItem(CLOSED_GROUPS_KEY, JSON.stringify([...next]));
  } catch {
    /* متصفّحٌ يمنع التخزين — يعمل الطيُّ هذه الجلسةَ ولا يُحفظ. */
  }
}

const RANK: Record<string, number> = {
  OWNER: 5,
  ADMIN: 4,
  MANAGER: 3,
  SALES: 2,
  EMPLOYEE: 1,
};

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'مالك الحساب',
  ADMIN: 'مدير النظام',
  MANAGER: 'مدير',
  SALES: 'مبيعات',
  EMPLOYEE: 'موظف',
};

function AvatarDot({
  name,
  src,
  big = false,
}: {
  name: string;
  src?: string | null;
  big?: boolean;
}) {
  return (
    <span className={`pf-avatar${big ? ' pf-avatar-lg' : ''}`}>
      {src ? (
        <img className="avatar avatar-img" src={src} alt={big ? '' : name} />
      ) : (
        <span className="avatar" title={big ? undefined : name}>
          {initials(name)}
        </span>
      )}
      {/* نقطةُ الاتصال: جلسةٌ نشطةٌ الآن. */}
      <span className="pf-online" title="متصل" aria-hidden />
    </span>
  );
}

/**
 * لوحةُ الحساب — تُفتح من الصورة، وفيها ما يخصّ صاحبها ملخّصًا.
 *
 * الصورةُ في الشريط بابٌ لا زينة: النقرةُ تفتح لوحةً فيها اسمُه وبريدُه
 * ودورُه، وباقةُ شركته وتاريخُ انتهائها، وروابطُ ملفّه وإعداداته، والخروجُ
 * في قاعها أحمرَ مفصولًا. وتُرسى في ركن الشاشة اليساريّ كالإشعارات، لا
 * معلَّقةً وسطَه.
 */
function ProfileMenu() {
  const { logout, atLeast } = useAuth();
  const session = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const user = session.user;
  const tenant = session.tenant;
  const status = TENANT_STATUS_MAP[tenant.status];
  const expiry = tenant.periodEndsAt ?? tenant.trialEndsAt;

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div className="pf-wrap" ref={wrap}>
      <button
        className="profile-trigger"
        aria-label="حسابي"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <AvatarDot name={user.name} src={user.avatarUrl} />
      </button>

      {open && (
        <div className="pf-panel" role="menu" aria-label="حسابي">
          <div className="pf-head">
            <AvatarDot name={user.name} src={user.avatarUrl} big />
            <div className="pf-name">{user.name}</div>
            <a className="pf-email num" href={`mailto:${user.email}`} dir="ltr">
              {user.email}
            </a>
            <div className="pf-tags">
              {session.workMode?.active ? (
                <Badge kind="warning">حساب فرعي: {session.workMode.active.name}</Badge>
              ) : (
                <Badge kind="info">{ROLE_LABELS[user.role] ?? user.role}</Badge>
              )}
              <span className="tiny mute">{tenant.name}</span>
            </div>
          </div>

          {/* التبديلُ بين الحساب الأصليّ والحسابات الفرعيّة — بنفس الدخول. */}
          <WorkModeSwitch workMode={session.workMode} onDone={() => setOpen(false)} />

          <div className="pf-links">
            <button className="pf-link" onClick={() => go('/account')}>
              <Icon name="user" size={15} />
              الملف الشخصي
            </button>
            <button className="pf-link" onClick={() => go('/settings')}>
              <Icon name="settings" size={15} />
              الإعدادات
            </button>
          </div>

          <div className="pf-sub">
            <div className="pf-sub-head">
              <span className="pf-sub-plan">
                <Icon name="wallet" size={15} />
                {tenant.planName ? `باقة ${tenant.planName}` : 'باقة تجريبية'}
              </span>
              {status && <Badge kind={status.kind}>{status.label}</Badge>}
            </div>
            {expiry && <div className="tiny mute">تنتهي في {date(expiry)}</div>}
            {atLeast('ADMIN') && (
              <button className="pf-sub-link" onClick={() => go('/account/subscription')}>
                إدارة الاشتراك والدفع
                <Icon name="chevronLeft" size={13} />
              </button>
            )}
          </div>

          <div className="pf-rows">
            <button className="pf-row" onClick={() => go('/settings/notifications')}>
              <Icon name="bell" size={15} />
              إعدادات الإشعارات
            </button>
            <button className="pf-row" onClick={() => go('/settings/screen-monitoring')}>
              <Icon name="eye" size={15} />
              إذن مراقبة الشاشة
            </button>
          </div>

          <button
            className="pf-signout"
            onClick={() => {
              setOpen(false);
              void logout();
            }}
          >
            <Icon name="logout" size={16} />
            تسجيل الخروج
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * زرُّ اللغة — بجوار الصورة.
 *
 * يبدّلُ لغةَ الواجهةِ بين العربيّةِ والإنجليزيّةِ فورًا، ويقلبُ اتجاهَ الصفحةِ
 * (RTL/LTR)، ويحفظُ التفضيلة. الأنجلزةُ تدريجيّةٌ: ما لم يُترجَم بعد يبقى عربيًّا.
 */
function LanguageMenu() {
  const { locale, setLocale } = useI18n();
  return (
    <Menu
      label="اللغة"
      className="topbar-gear lang-btn"
      actions={[
        {
          key: 'ar',
          label: 'العربية',
          icon: locale === 'ar' ? 'check' : 'globe',
          onSelect: () => setLocale('ar'),
        },
        {
          key: 'en',
          label: 'English',
          icon: locale === 'en' ? 'check' : 'globe',
          onSelect: () => setLocale('en'),
        },
      ]}
    >
      <Icon name="globe" size={17} />
      <span className="lang-code">{locale === 'en' ? 'EN' : 'ع'}</span>
    </Menu>
  );
}

export function Shell() {
  const { hasFeature, can } = useAuth();
  const { t } = useI18n();
  const session = useSession();
  const location = useLocation();
  const [sideOpen, setSideOpen] = useState(false);
  /* طيُّ القائمةِ الجانبيّةِ إلى أيقوناتٍ فقط — يُحفظُ ولا يُنسى. */
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('goyone.sidebar.collapsed') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('goyone.sidebar.collapsed', collapsed ? '1' : '0');
    } catch {
      /* تجاهل */
    }
  }, [collapsed]);

  const app = appForPath(location.pathname);

  /* الجداول ذات الرأس الثابت تُقاس على ما يتّسع تحت شريط الأدوات — انظر stickyTableFit. */
  const mainRef = useRef<HTMLElement>(null);
  useStickyTableFit(mainRef);

  /* إغلاق القائمة الجانبية عند التنقّل: تركها مفتوحة على الجوال يحجب المحتوى. */
  useEffect(() => setSideOpen(false), [location.pathname]);

  /*
   * نبضة نشاطٍ ما دام مسجَّل الدخول.
   *
   * تُخبر الخادم بالشاشة الحالية كل ثوانٍ، فيعرف المدير أين يعمل كلُّ موظفٍ
   * لحظةً بلحظة. بلا إذنٍ ولا مشاركة شاشة — والمشاركة طبقةٌ فوقها للصورة
   * الحيّة، يبدؤها الموظف بنفسه.
   */
  useActivityHeartbeat(Boolean(session.user));

  /*
   * المجموعاتُ المطويّة تُحفظ ولا تُنسى.
   *
   * قسمٌ فيه عشرُ مجموعاتٍ يُطوى أكثرُه عادةً، ومن طواه لا يريد أن يطويه
   * ثانيةً بعد كلّ تنقّل. والمحفوظُ هو **المطويّ** لا المفتوح: المفتوحُ هو
   * الأصل، فمن فتح النظامَ أوّلَ مرّةٍ يرى قسمَه كاملًا لا قائمةً مغلقة.
   */
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(CLOSED_GROUPS_KEY);
      return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      /* تخزينٌ محظورٌ أو تالف: تُفتح كلُّها — وهو الأصل. */
      return new Set<string>();
    }
  });

  const toggleGroup = (key: string, open: boolean) => {
    setClosedGroups((prev) => {
      const next = new Set(prev);
      if (open) next.add(key);
      else next.delete(key);
      writeClosed(next);
      return next;
    });
  };

  /*
   * الوصولُ إلى شاشةٍ داخل مجموعةٍ مطويّةٍ يفتحها.
   *
   * ومن يطويها بنقرةٍ وهو فيها تبقى مطويّةً: كان الحارسُ يمنع طيَّها
   * أصلًا، فتذهب النقرةُ بلا أثرٍ مرئيّ ويظنّ المستخدمُ الزرَّ معطوبًا.
   * الفتحُ عند **الوصول** يحفظ الغرضَ — ألّا يُفتح رابطٌ فلا يُرى أين هو —
   * ولا يبتلع نقرةً قصدَها صاحبُها.
   */
  useEffect(() => {
    /*
     * أطولُ مسارٍ مطابقٍ هو الصاحب.
     *
     * `/hr` بادئةُ كلّ مسارٍ في القسم، فأوّلُ مطابقةٍ كانت تقع دائمًا على
     * مجموعة «الهيكل» — فتُفتح هي ويبقى مسارُ التوظيف مطويًّا وفيه الشاشةُ
     * المفتوحة.
     */
    let inside: (typeof app.groups)[number] | null = null;
    let longest = -1;

    for (const group of app.groups) {
      for (const item of group.items) {
        const hit =
          location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
        if (hit && item.path.length > longest) {
          inside = group;
          longest = item.path.length;
        }
      }
    }

    if (!inside) return;

    const key = `${app.key}:${inside.title}`;
    setClosedGroups((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      writeClosed(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, app.key]);

  /*
   * ملفُّ الصلاحيات يُخفي ما مستواه «لا شيء»: التطبيقَ كلَّه، أو مجموعةً،
   * أو عنصرًا. وما يخصّ الموظّفَ عن نفسه () يبقى — فيظهر تطبيقُه
   * ولو أُغلقت وحدتُه.
   */
  const visibleApps = APPS.filter(
    (a) =>
      (!a.feature || hasFeature(a.feature)) &&
      (!a.module || can(a.module) || a.groups.some((g) => g.always)),
  );
  /* الإعداداتُ ترسٌ و«حسابي» يُفتح من الصورة — فلا يظهران تطبيقين في الشريط. */
  const navApps = visibleApps.filter((a) => a.key !== 'settings' && a.key !== 'account');
  const status = TENANT_STATUS_MAP[session.tenant.status];
  const readOnly = status.access !== 'full';

  return (
    <ScreenMonitorProvider>
      <div className={`shell${collapsed ? ' side-collapsed' : ''}`}>
        <header className="topbar">
          <button
            className="btn btn-icon btn-ghost menu-toggle"
            onClick={() => setSideOpen((v) => !v)}
            aria-label="القائمة"
            aria-expanded={sideOpen}
          >
            <Icon name="menu" size={18} />
          </button>

          <Link to="/" className="brand">
            <Logo size={26} />
            <span>GoyOne</span>
          </Link>

          <nav className="apps" aria-label="التطبيقات">
            {navApps.map((a) => (
              <NavLink
                key={a.key}
                to={a.base}
                className={`app-link${a.soon ? ' app-soon' : ''}${
                  a.key === app.key ? ' active' : ''
                }`}
                aria-current={a.key === app.key ? 'page' : undefined}
              >
                {t(a.label)}
                {/*
                الإشارة إلى أن الوحدة قيد الإنشاء تُكتب نصًا لا في سمة title:
                السمة تحلّ محل نص الرابط عند قارئ الشاشة، فيسمع المستخدم
                "قيد الإنشاء" ولا يعرف أي تطبيق هذا.
              */}
                {a.soon && <span className="app-soon-tag">{t('قيد الإنشاء')}</span>}
              </NavLink>
            ))}
          </nav>

          {/*
          الشعارُ يمينًا (بدءُ الشريط في العربية) والحسابُ يسارًا (طرفُه).
          والطرفُ يُقرأ من اليمين: الحضورُ فالمؤقّتُ فالإشعاراتُ فترسُ
          الإعدادات، ثمّ الملفُّ في أقصى اليسار.
        */}
          <div className="topbar-end">
            {/*
            المؤقّت وزرُّ الحضور في الشريط لا في شاشة.
            من نسي أنه يعمل لن يفتح شاشةً ليتذكّر، ومن ينصرف يغلق ما بين
            يديه — وزرٌّ في صفحةٍ لا تُفتح زرٌّ لا يُضغط.
          */}
            {/* شارةُ الحساب الفرعيّ النشط — ظاهرةٌ دائمًا فلا ينسى في أيّ وضعٍ هو. */}
            <WorkModeBadge workMode={session.workMode} />
            <CheckClock />
            <WorkTimerBadge />
            {readOnly && (
              <span className={`badge badge-${status.kind}`} title="الوصول للقراءة فقط">
                {status.label}
              </span>
            )}
            <NotificationBell />
            {/* الإعداداتُ ترسٌ بجوار الملفّ لا كلمةً في شريط التطبيقات. */}
            <Link
              to="/settings"
              className={`topbar-gear${app.key === 'settings' ? ' is-active' : ''}`}
              title="الإعدادات"
              aria-label="الإعدادات"
              aria-current={app.key === 'settings' ? 'page' : undefined}
            >
              <Icon name="settings" size={18} />
            </Link>
            <LanguageMenu />
            <span className="topbar-divider" aria-hidden />
            <ProfileMenu />
          </div>
        </header>

        <NotificationPopups />

        {sideOpen && <div className="scrim" onClick={() => setSideOpen(false)} />}

        <aside
          className={`sidebar${sideOpen ? ' open' : ''}${collapsed ? ' is-collapsed' : ''}`}
          aria-label="أقسام التطبيق"
        >
          {/* طيُّ القائمةِ إلى أيقوناتٍ فقط، أو فردُها — على سطحِ المكتب. */}
          <button
            type="button"
            className="side-collapse-btn"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'فرد القائمة' : 'طيّ القائمة إلى أيقونات'}
            aria-label={collapsed ? 'فرد القائمة' : 'طيّ القائمة'}
            aria-pressed={collapsed}
          >
            <Icon name={collapsed ? 'chevronLeft' : 'chevronRight'} size={16} />
          </button>

          <div className="side-scroll">
          {app.groups.map((group) => {
            const groupModule = group.module ?? app.module;
            if (!group.always && groupModule && !can(groupModule)) return null;
            const items = group.items.filter(
              (i) =>
                (!i.minRole || RANK[session.user.role]! >= RANK[i.minRole]!) &&
                (!i.feature || hasFeature(i.feature)) &&
                (!i.module || can(i.module)),
            );
            if (items.length === 0) return null;

            /* كل مسارات التطبيق — بها يُعرف أيّ عنصر يمتدّ منه غيره. */
            const allPaths = app.groups.flatMap((g) => g.items.map((i) => i.path));

            const key = `${app.key}:${group.title}`;
            const open = !closedGroups.has(key);

            return (
              <div className={`side-group${open ? '' : ' is-closed'}`} key={group.title}>
                <button
                  type="button"
                  className="side-title"
                  aria-expanded={open}
                  onClick={() => toggleGroup(key, open)}
                >
                  <span>{t(group.title)}</span>
                  <Icon name={open ? 'chevronDown' : 'chevronLeft'} size={13} />
                </button>

                {(open || collapsed) &&
                  items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={
                        item.path === app.base ||
                        allPaths.some((other) => other.startsWith(`${item.path}/`))
                      }
                      className={`side-link${item.soon ? ' app-soon' : ''}`}
                      title={collapsed ? t(item.label) : undefined}
                    >
                      <span className="row">
                        {item.icon && <Icon name={item.icon} size={16} />}
                        <span className="side-label">{t(item.label)}</span>
                      </span>
                      {item.soon && <span className="app-soon-tag">{t('قريبًا')}</span>}
                    </NavLink>
                  ))}
              </div>
            );
          })}
          </div>
        </aside>

        {/*
        القسمُ المفتوح يُعلَن على الهيكل.

        بعضُ القرارات البصرية تخصّ قسمًا بعينه — كإلغاء تسطير الروابط في
        إدارة المشاريع — ولا سبيل إلى كتابتها في CSS بلا مِرساة. والبديلُ
        أن يحمل كلُّ ملفِّ شاشةٍ صنفًا يكرّره، فيُنسى في الشاشة التالية.
      */}
        <main ref={mainRef} className="main" data-app={app.key}>
          {/*
          اللافتة تغطي دورة الاشتراك كلها لا حالة القراءة فقط: التجربة
          التي تقترب من نهايتها تحتاج تنبيهًا قبل أن تتوقف، لا بعده.
        */}
          <SubscriptionBanner />
          {/*
          الحاجز داخل الهيكل لا حوله: خطأٌ في شاشة يترك الشريط والقائمة
          يعملان، فينتقل المستخدم إلى غيرها بدل أن يجد شاشة بيضاء لا يعرف
          أنظامٌ سقط أم جلسةٌ خرجت.
        */}
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>

        {/*
        الشاتُ فقاعةٌ فوق كلّ شاشة لا صفحةٌ تُترك لها.
        من يسأل زميلًا وهو في فاتورةٍ لا يغادرها ليكتب سؤاله ثم يعود يبحث
        عن موضعه. وهي في الهيكل فتبقى المحادثةُ مفتوحةً بين التنقّلات.
      */}
        <ChatDock />
        <AppFooter />
      </div>
    </ScreenMonitorProvider>
  );
}

/** زرُّ الشاتِ في الشريطِ السفليّ — يفتحُ لوحةَ المحادثاتِ ويُظهرُ غيرَ المقروء. */
function FooterChatButton() {
  const list = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => api<Array<{ unread: number }>>('/chat/conversations'),
    refetchInterval: 45_000,
  });
  const total = (list.data ?? []).reduce((sum, c) => sum + (c.unread || 0), 0);
  const [bump, setBump] = useState(false);

  useEffect(() => {
    const onBump = () => {
      setBump(true);
      window.setTimeout(() => setBump(false), 1200);
    };
    window.addEventListener('goyone:chat-bump', onBump);
    return () => window.removeEventListener('goyone:chat-bump', onBump);
  }, []);

  return (
    <button
      className={`foot-btn foot-chat${bump ? ' is-bump' : ''}`}
      onClick={() =>
        window.dispatchEvent(new CustomEvent('goyone:chat-open', { detail: { view: 'team' } }))
      }
      title={total > 0 ? `محادثات الفريق — ${total} غير مقروءة` : 'محادثات الفريق'}
      aria-label="محادثات الفريق"
    >
      <Icon name="chat" size={16} />
      {total > 0 && <span className="foot-chat-badge num">{total > 99 ? '99+' : total}</span>}
    </button>
  );
}

/**
 * زرُّ جوي في الشريطِ السفليّ — يفتحُ المساعدَ الذكيّ مباشرةً.
 *
 * جوي لمديرِ النظامِ وحدَه: أدواتُه تقرأ الشركةَ كلَّها والخادمُ يرفض من
 * دونه — فلا يُعرض زرُّه لمن لا يملكه.
 */
function FooterJoyButton() {
  const { atLeast } = useAuth();
  if (!atLeast('ADMIN')) return null;
  return (
    <button
      className="foot-btn foot-joy"
      onClick={() =>
        window.dispatchEvent(new CustomEvent('goyone:chat-open', { detail: { view: 'joy' } }))
      }
      title="جوي — مساعدُك الذكيّ"
      aria-label="جوي — المساعد الذكيّ"
    >
      <JoyMark size={20} />
    </button>
  );
}

/**
 * الشريطُ السفليُّ الدائم — مساحةُ أمانٍ ترفعُ المحتوى فوقَها، وفيها المساعدةُ
 * وأدواتٌ سريعةٌ كما في أنظمةٍ مثلِ زوهو.
 */
function AppFooter() {
  const { t } = useI18n();
  const [help, setHelp] = useState(false);
  /* إصدارُ تطبيقِ سطح المكتب المثبَّت — به يُعرف أيُّ نسخةٍ تعمل. وفي المتصفّح: «ويب». */
  const [ver, setVer] = useState<string | null>(null);
  useEffect(() => {
    if (!desktop) return;
    void desktop.appVersion?.().then((v) => setVer(v)).catch(() => undefined);
  }, []);
  return (
    <footer className="app-footer">
      <span className="foot-brand">
        <span className="foot-dot" aria-hidden />
        <span>GoyOne</span>
        <span className="foot-sep">·</span>
        <span className="foot-ver tiny num">{desktop ? (ver ? `v${ver}` : 'v0.1.4 أو أقدم') : 'ويب'}</span>
      </span>

      <span className="foot-tools">
        <FooterJoyButton />
        <FooterChatButton />
        <Link
          className="foot-btn"
          to="/settings/desktop-app"
          title={t('تطبيق سطح المكتب')}
          aria-label={t('تطبيق سطح المكتب')}
        >
          <Icon name="download" size={15} />
        </Link>
        <Link className="foot-btn" to="/settings" title={t('الإعدادات')} aria-label={t('الإعدادات')}>
          <Icon name="settings" size={15} />
        </Link>
        <button
          className="foot-btn"
          onClick={() => setHelp(true)}
          title={t('المساعدة')}
          aria-label={t('المساعدة')}
        >
          <Icon name="info" size={16} />
        </button>
      </span>

      {help && (
        <Modal title={t('المساعدة والدعم')} onClose={() => setHelp(false)}>
          <div className="col" style={{ gap: 'var(--sp-4)' }}>
            <div>
              <h4 style={{ margin: '0 0 6px' }}>الدعم الفنّي</h4>
              <p className="small mute" style={{ margin: 0 }}>
                فريقُ GoyOne جاهزٌ لمساعدتك — راسلنا أو اتّصل بنا:
              </p>
              <div className="row" style={{ gap: 'var(--sp-3)', marginTop: 8, flexWrap: 'wrap' }}>
                <a className="btn btn-sm" href="mailto:support@goyone.com">
                  <Icon name="mail" size={14} /> support@goyone.com
                </a>
                <a className="btn btn-sm num" dir="ltr" href="tel:+201146293399">
                  <Icon name="phone" size={14} /> +20 114 629 3399
                </a>
              </div>
            </div>
            <div>
              <h4 style={{ margin: '0 0 6px' }}>عن النظام</h4>
              <p className="small mute" style={{ margin: 0 }}>
                GoyOne — نظامُ إدارةِ أعمالٍ متكاملٌ باللغة العربية. الإصدار{' '}
                <span className="num" dir="ltr">
                  1.0
                </span>
                .
              </p>
            </div>
          </div>
        </Modal>
      )}
    </footer>
  );
}
